import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileCheck, Plus, ShieldAlert, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import EntityDialog from "@/pages/people/EntityDialog";

const SIT = ["standard", "separes", "garde_alternee", "decision_judiciaire", "parent_non_autorise"];

const FILTERS = [
  { key: "all", label: "allFilter" },
  { key: "to_complete", label: "filterToComplete" },
  { key: "to_verify", label: "filterToVerify" },
  { key: "missing", label: "filterMissing" },
  { key: "refused", label: "filterRefused" },
  { key: "ready_admin", label: "filterReadyAdmin" },
  { key: "needs_direction", label: "filterNeedsDirection" },
  { key: "ready_finalize", label: "filterReadyFinalize" },
  { key: "finalized", label: "filterFinalized" },
  { key: "blocked", label: "filterBlocked" },
  { key: "refused_abandoned", label: "filterRefusedAbandoned" },
];

export default function AdmissionsDirectory() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canManage = hasRole("super_admin", "direction", "administration");
  const isStaff = hasRole("super_admin", "direction", "administration");
  const [rows, setRows] = useState([]);
  const [dash, setDash] = useState(null);
  const [families, setFamilies] = useState([]);
  const [levels, setLevels] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const loadDash = () => api.get("/admissions/dashboard").then((r) => setDash(r.data)).catch(() => {});
  const load = () => api.get("/admissions").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  useEffect(() => {
    load(); loadDash();
    api.get("/people/families").then((r) => setFamilies(r.data)).catch(() => {});
    api.get("/school/levels").then((r) => setLevels(r.data.items || r.data)).catch(() => {});
  }, []);
  const create = async (v) => { try { await api.post("/admissions", v); toast.success(t("save")); load(); loadDash(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admissions/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "inscriptions.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const match = (d) => {
    const s = d.doc_summary || {};
    switch (filter) {
      case "to_complete": return ["Brouillon", "En attente pièces", "Incomplet"].includes(d.status);
      case "to_verify": return d.status === "À vérifier";
      case "missing": return (s.missing || 0) > 0;
      case "refused": return (s.refused || 0) > 0;
      case "ready_admin": return d.status === "Complet";
      case "needs_direction": return d.status === "En attente validation Direction";
      case "ready_finalize": return d.status === "Validé";
      case "finalized": return d.status === "Finalisé";
      case "blocked": return !!d.blocked;
      case "refused_abandoned": return ["Refusé", "Abandonné"].includes(d.status);
      default: return true;
    }
  };
  let filtered = rows.filter(match);
  if (filter === "blocked") filtered = [...filtered].sort((a, b) => (b.days_in_status || 0) - (a.days_in_status || 0));

  const Stat = ({ k, value, fkey }) => (
    <button data-testid={`dash-stat-${k}`} disabled={!fkey} onClick={() => fkey && setFilter(fkey)}
      className={`rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-colors ${fkey ? "hover:border-sky-300 cursor-pointer" : "cursor-default"}`}>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{t(k)}</div>
    </button>
  );

  return (
    <div data-testid="admissions-directory">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("admissions")}</h1>
        <div className="flex items-center gap-2">
          {isStaff && <Button variant="outline" className="gap-2" data-testid="export-csv-btn" onClick={exportCsv}><Download className="h-4 w-4" />{t("exportCsv")}</Button>}
          {canManage && <Button className="gap-2" data-testid="add-admission-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" />{t("newAdmission")}</Button>}
        </div>
      </div>

      {dash && (
        <div className="mb-5" data-testid="admissions-dashboard">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">{t("admissionsDashboard")}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <Stat k="toProcess" value={dash.to_process} fkey="all" />
            <Stat k="toValidate" value={dash.to_validate} fkey="ready_admin" />
            <Stat k="filterIncomplete" value={dash.incomplete} fkey="to_complete" />
            <Stat k="readyToFinalize" value={dash.ready_to_finalize} fkey="ready_finalize" />
            <Stat k="awaitingDirection" value={dash.awaiting_direction} fkey="needs_direction" />
            <Stat k="withRefusedDocs" value={dash.with_refused_docs} fkey="refused" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Entonnoir */}
            <Card className="border-slate-200 p-4 lg:col-span-2" data-testid="admissions-funnel">
              <p className="text-sm font-semibold text-slate-800 mb-3">{t("funnel")}</p>
              <div className="space-y-1.5">
                {(dash.funnel || []).filter((f) => f.count > 0 || ["Brouillon", "Complet", "Validé", "Finalisé"].includes(f.status)).map((f) => {
                  const max = Math.max(1, ...(dash.funnel || []).map((x) => x.count));
                  return (
                    <button key={f.status} data-testid={`funnel-${f.status}`} onClick={() => setFilter("all")}
                      className="w-full flex items-center gap-2 group">
                      <span className="w-48 shrink-0 text-xs text-slate-600 text-right pr-2">{f.status}</span>
                      <div className="flex-1 h-5 rounded bg-slate-100 overflow-hidden">
                        <div className="h-full bg-sky-500/80 group-hover:bg-sky-600 transition-all" style={{ width: `${(f.count / max) * 100}%` }} />
                      </div>
                      <span className="w-8 text-xs font-semibold text-slate-700 text-right">{f.count}</span>
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Dossiers bloqués */}
            <Card className="border-amber-200 bg-amber-50/40 p-4" data-testid="blocked-card">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />{t("blockedFiles")}</p>
                <Badge className="bg-amber-600 text-white border-0" data-testid="blocked-count">{dash.blocked_count}</Badge>
              </div>
              {dash.blocked_count === 0 ? (
                <p className="text-xs text-slate-500">{t("noBlocked")}</p>
              ) : (
                <div className="space-y-1.5">
                  {(dash.blocked_top || []).map((b) => (
                    <Link key={b.id} to={`/admin/admissions/${b.id}`} data-testid={`blocked-item-${b.id}`}
                      className="flex items-center justify-between rounded-lg bg-white px-2.5 py-1.5 hover:border-amber-300 border border-transparent">
                      <div className="min-w-0">
                        <div className="text-xs font-mono text-slate-700 truncate">{b.file_number}</div>
                        <div className="text-[11px] text-slate-500 truncate">{b.blocked_reason} · {b.status}</div>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700 border-0 shrink-0">{b.days_in_status}{t("days")}</Badge>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="admissions-filters">
        {FILTERS.map((f) => (
          <Button key={f.key} size="sm" variant={filter === f.key ? "default" : "outline"} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)}>{t(f.label)}</Button>
        ))}
      </div>

      <EntityDialog open={open} onOpenChange={setOpen} title={t("newAdmission")} submitLabel={t("create")} onSubmit={create}
        fields={[
          { name: "type", label: t("dossierType"), type: "select", options: [{ value: "inscription", label: t("inscription") }, { value: "reinscription", label: t("reinscription") }] },
          { name: "family_id", label: t("family"), type: "select", options: families.map((f) => ({ value: f.id, label: f.name })) },
          { name: "student_name", label: t("studentName"), type: "text" },
          { name: "level_id", label: t("level"), type: "select", options: levels.map((l) => ({ value: l.id, label: l.name })) },
          { name: "family_situation", label: t("familySituation"), type: "select", options: SIT.map((s) => ({ value: s, label: t(`sit_${s}`) })) },
        ]} />

      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead>{t("fileNumber")}</TableHead><TableHead>{t("dossierType")}</TableHead><TableHead>{t("family")}</TableHead>
            <TableHead>{t("level")}</TableHead><TableHead>{t("checklist")}</TableHead><TableHead>{t("blockedSince")}</TableHead>
            <TableHead>{t("status")}</TableHead><TableHead className="text-right">{t("viewFile")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((d) => {
              const s = d.doc_summary || {};
              return (
              <TableRow key={d.id} data-testid="admission-row">
                <TableCell className="font-mono text-xs text-slate-700">{d.file_number}
                  {d.requires_direction_validation && <Badge className="ml-2 bg-amber-100 text-amber-700 border-0 gap-1"><ShieldAlert className="h-3 w-3" />{t("needsDirection")}</Badge>}
                </TableCell>
                <TableCell className="text-slate-600">{t(d.type)}</TableCell>
                <TableCell className="text-slate-500">{d.family_name || "—"}</TableCell>
                <TableCell className="text-slate-500">{d.level_name || "—"}</TableCell>
                <TableCell data-testid={`missing-counter-${d.id}`}>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-medium text-slate-700">{s.validated || 0}/{s.required_total || 0}</span>
                    {(s.missing || 0) > 0 && <Badge className="bg-rose-50 text-rose-600 border-0 text-xs">{s.missing} {t("missingDocs")}</Badge>}
                    {(s.refused || 0) > 0 && <Badge className="bg-red-100 text-red-700 border-0 text-xs">{s.refused} {t("docsRefused")}</Badge>}
                  </div>
                </TableCell>
                <TableCell data-testid={`blocked-cell-${d.id}`}>
                  {d.blocked
                    ? <Badge className="bg-amber-600 text-white border-0 gap-1"><AlertTriangle className="h-3 w-3" />{d.days_in_status}{t("days")}</Badge>
                    : <span className="text-xs text-slate-400">{d.days_in_status || 0}{t("days")}</span>}
                </TableCell>
                <TableCell><Badge className="bg-slate-100 text-slate-600 border-0">{d.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Link to={`/admin/admissions/${d.id}`} data-testid={`open-admission-${d.id}`}>
                    <Button size="sm" variant="outline" className="gap-2"><FileCheck className="h-4 w-4" />{t("viewFile")}</Button>
                  </Link>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
